import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldArray, Field } from 'formik';
import { Icon } from '@pwc/pwc-deals-ui';
import { v4 as uuidv4 } from 'uuid';
import MultiSelectSearchWithLabels from '../../../../common/components/MultiSelectSearchWithLabels/MultiSelectSearchWithLabels';
import { useClickedOutsideOfRef } from '../../../../common/hooks/useClickedOutsideOfRef';
import { keyParser } from '../../../../common/engine/utilities/linkedAdjustmentsUtilities';
import { CloseButtonSearch } from './LinkedAdjustmentsSelectedItems/components/CloseButtonSearch';
import LinkedAdjustmentsSelectedItems from './LinkedAdjustmentsSelectedItems/LinkedAdjustmentsSelectedItems';
import './LinkedAdjustmentsEditor.scss';

const LinkedAdjustmentsEditor = ({
  projectId,
  sourceAdjustment,
  adjustmentOptions,
  targetDocumentOptions,
  selectedLinkedAdjustments = [],
  adjustmentLinks,
  setFieldValue,
  tagsRight,
  allowCreate,
  placeholderText,
  openDropDown,
}) => {
  const FIELD_NAME = 'linkedAdjustments';
  const [loading, setLoading] = useState(true);
  const [selectedAdjustmentsValue, setSelectedAdjustmentsValue] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState();
  const [dropdownOpen, setDropdownOpen] = useState(openDropDown);

  const handleClearSearch = () => {
    setSearch('');
    setDropdownOpen(false);
  };

  useEffect(() => {
    setError();
    setLoading(false);
    const selectedAdjustments = selectedLinkedAdjustments.map(
      (value) => value.targetAdjustment
    );
    setSelectedAdjustmentsValue(selectedAdjustments);
  }, [selectedLinkedAdjustments, loading]);

  const handleEscapeKey = (ev) => {
    if (ev.keyCode === 27) {
      setDropdownOpen(false);
    }
  };

  const adjustmentSearchOptions = (targetDocumentOptions || []).map(
    (target) => {
      const items = adjustmentOptions[target.value].map((adj) => {
        return { text: adj.label, value: adj.value };
      });
      return { header: target.label, items };
    }
  );

  const getSelectedLinks = () => {
    const selectedLinks = adjustmentLinks.map((al) => {
      const { isBroken, target } = al;
      const {
        documentCode,
        adjustmentType,
        adjustmentNumber,
        adjustmentDisplayName,
        queryString,
      } = target;
      const name = adjustmentDisplayName.replace(adjustmentNumber, '').trim();
      const value = `${documentCode}_${adjustmentType}_${adjustmentNumber}_${name}`;
      const item = {
        value,
        queryString,
        isBroken,
        documentCode,
        displayName: adjustmentDisplayName,
      };
      const found = selectedLinkedAdjustments.find(
        (sl) => sl.targetAdjustment === value
      );
      if (found) {
        return item;
      }
      return null;
    });
    const newItems = selectedLinkedAdjustments
      .filter((sa) => !!sa.newId)
      .map((sa) => {
        const { number, name, code } = keyParser(sa.targetAdjustment);
        return {
          value: sa.targetAdjustment,
          displayName: `${number} ${name}`,
          documentCode: code,
          queryString: '',
          newId: sa.newId,
        };
      });
    return (targetDocumentOptions || [])
      .map((target) => {
        const links = [...selectedLinks, ...newItems].filter(
          (sl) => sl && sl.documentCode === target.value
        );
        if (links.length > 0) {
          return {
            documentCode: target.value,
            documentDisplayName: target.label,
            links,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const onSubmit = (updatedSelectedAdjustmentStringValue) => {
    const optionsList = Array.prototype.concat.apply(
      [],
      adjustmentSearchOptions.map((op) => op.items)
    );
    const allSelectedItems = optionsList.filter((opt) =>
      updatedSelectedAdjustmentStringValue.includes(opt.value)
    );
    const itemsToSave = allSelectedItems.map((item) => {
      const { value } = item;
      const linkedAdjustment = selectedLinkedAdjustments.find((i) => {
        const { targetAdjustment } = i;
        return targetAdjustment === value;
      });
      if (!linkedAdjustment) {
        return {
          newId: uuidv4(),
          sourceAdjustment: sourceAdjustment.value,
          targetAdjustment: item.value,
        };
      }
      return linkedAdjustment;
    });
    setSelectedAdjustmentsValue(updatedSelectedAdjustmentStringValue);
    setFieldValue(FIELD_NAME, itemsToSave, sourceAdjustment.value);
    setDropdownOpen(false);
  };

  const onDelete = (link) => {
    const adjustmentsToUpdate = selectedLinkedAdjustments.filter(
      (adj) => adj.targetAdjustment !== link.value || adj.newId !== link.newId
    );
    setFieldValue(FIELD_NAME, adjustmentsToUpdate, sourceAdjustment.value);
  };

  const ref = useClickedOutsideOfRef(
    (wasClickedOutside) =>
      wasClickedOutside && setDropdownOpen(!wasClickedOutside)
  );

  const getSelectedPills = () => {
    const selectedLinks = getSelectedLinks();

    return (
      <LinkedAdjustmentsSelectedItems
        projectId={projectId}
        selectedLinks={selectedLinks}
        onDelete={onDelete}
        canDelete
      />
    );
  };
  return (
    <div className="link-adjustments-editor" tabIndex="-1">
      <FieldArray
        name="linked-adjustments"
        render={() => (
          <>
            <div className="a-form-error">{error}</div>
            <div className="title">Link Adjustment</div>
            <div
              className="input-field-container"
              data-tags-right={`${tagsRight}`}
              ref={ref}
            >
              <div className="search-container">
                <span className="filter-search-icon">
                  <Icon
                    name="search"
                    width={18}
                    className="outline icon-grey icon-margin-right"
                  />
                </span>
                {dropdownOpen && (
                  <CloseButtonSearch onClick={handleClearSearch} />
                )}
                <Field
                  id="link-adjustment-filter-search"
                  name="adjustment"
                  className={`input-field ${dropdownOpen ? 'active' : ''}`}
                  placeholder={placeholderText}
                  error={error}
                  autoComplete="off"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  data-testid="text-input-linked-adjustment-search"
                  onFocus={() => {
                    setDropdownOpen(true);
                  }}
                  onKeyDown={(e) => handleEscapeKey(e)}
                />
                {dropdownOpen && (
                  <MultiSelectSearchWithLabels
                    isLoading={loading}
                    searchCriteria={search}
                    options={adjustmentSearchOptions}
                    onSubmit={onSubmit}
                    selectedLinkedAdjustments={selectedAdjustmentsValue}
                    allowCreate={allowCreate}
                    innerIdWhitelist={['link-adjustment-filter-search']}
                    closeDropdown={() => setDropdownOpen(false)}
                    includeHeaders
                    applyButtonText="Link adjustments"
                    applyButtonIcon="link"
                  />
                )}
              </div>
              <div className="selected-adjustment-links">
                {getSelectedPills()}
              </div>
            </div>
          </>
        )}
      />
    </div>
  );
};

LinkedAdjustmentsEditor.propTypes = {
  tagsRight: PropTypes.bool,
  allowCreate: PropTypes.bool,
  placeholderText: PropTypes.string,
  sourceAdjustment: PropTypes.exact({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedLinkedAdjustments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      newId: PropTypes.string,
      sourceAdjustment: PropTypes.string,
      targetAdjustment: PropTypes.string,
    })
  ),
  setFieldValue: PropTypes.func.isRequired,
};

LinkedAdjustmentsEditor.defaultProps = {
  tagsRight: false,
  allowCreate: false,
  placeholderText: 'Search and link an adjustment',
  sourceAdjustment: {},
  selectedLinkedAdjustments: [],
};

export default LinkedAdjustmentsEditor;
